import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { MessageSquare, Save, Search, CheckCircle2, AlertCircle } from 'lucide-react';
import apiService from '@/services/api';

export default function PrincipalCommentsPage() {
  const [students, setStudents] = useState([]);
  const [selectedYear, setSelectedYear] = useState('11');
  const [selectedTerm, setSelectedTerm] = useState('Term 1');
  const [comments, setComments] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [searchTerm, setSearchTerm] = useState('');

  const years = ['11', '12', '13'];
  const terms = ['Term 1', 'Term 2', 'Term 3', 'Term 4'];

  useEffect(() => {
    fetchStudents();
  }, [selectedYear]);

  const fetchStudents = async () => {
    try {
      setLoading(true);
      const response = await apiService.getStudents();
      const yearStudents = response.filter(
        s => s.department === `Year ${selectedYear} Arts` || 
             s.department === `Year ${selectedYear} Science` ||
             s.department === `Year ${selectedYear} Commerce`
      );
      setStudents(yearStudents);
    } catch (error) {
      console.error('Error fetching students:', error);
      setMessage({ type: 'error', text: 'Failed to load students' });
    } finally {
      setLoading(false);
    }
  };

  const handleCommentChange = (studentId, value) => {
    setComments(prev => ({
      ...prev,
      [studentId]: value
    }));
  };

  const saveComment = async (studentId) => {
    try {
      setSaving(true);
      const comment = comments[studentId];
      
      if (!comment || comment.trim() === '') {
        setMessage({ type: 'error', text: 'Please enter a comment' });
        return;
      }

      // API call to save principal comment (to be implemented on backend)
      await apiService.savePrincipalComment({
        student_id: studentId,
        year: parseInt(selectedYear),
        term: selectedTerm,
        comment: comment
      });

      setMessage({ type: 'success', text: 'Comment saved successfully!' });
      
      // Clear message after 3 seconds
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error saving comment:', error);
      setMessage({ type: 'error', text: 'Failed to save comment' });
    } finally {
      setSaving(false);
    }
  };

  const filteredStudents = students.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <div className="flex items-center gap-3 mb-2">
            <MessageSquare className="h-8 w-8 text-indigo-600" />
            <h1 className="text-3xl font-bold text-gray-900">Principal Comments Management</h1>
          </div>
          <p className="text-gray-600">Add comments to student report cards</p>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="pt-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Year Level</label>
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {years.map(year => (
                    <option key={year} value={year}>Year {year}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Term</label>
                <select
                  value={selectedTerm}
                  onChange={(e) => setSelectedTerm(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                >
                  {terms.map(term => (
                    <option key={term} value={term}>{term}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Search Student</label>
                <div className="relative">
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name..."
                    className="w-full p-2 pl-10 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Message Alert */}
        {message.text && (
          <Alert variant={message.type === 'error' ? 'destructive' : 'default'} className="mb-6">
            {message.type === 'success' ? (
              <CheckCircle2 className="h-4 w-4" />
            ) : (
              <AlertCircle className="h-4 w-4" />
            )}
            <AlertDescription>{message.text}</AlertDescription>
          </Alert>
        )}

        {/* Students List */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading students...</p>
          </div>
        ) : filteredStudents.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <p className="text-gray-500">No students found for Year {selectedYear}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {filteredStudents.map((student) => (
              <Card key={student.id} className="hover:shadow-lg transition-shadow">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50">
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg">{student.name}</CardTitle>
                      <CardDescription>{student.department}</CardDescription>
                    </div>
                    <Badge variant="outline" className="bg-white">
                      {selectedYear} - {selectedTerm}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent className="pt-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Principal&apos;s Comment
                  </label>
                  <textarea
                    value={comments[student.id] || ''}
                    onChange={(e) => handleCommentChange(student.id, e.target.value)}
                    placeholder="Enter your comment for this student's report card..."
                    rows={4}
                    className="w-full p-3 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 resize-none"
                  />
                  <div className="flex justify-between items-center mt-4">
                    <p className="text-sm text-gray-500">
                      {comments[student.id]?.length || 0} characters
                    </p>
                    <Button
                      onClick={() => saveComment(student.id)}
                      disabled={saving || !comments[student.id]}
                      className="bg-indigo-600 hover:bg-indigo-700"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {saving ? 'Saving...' : 'Save Comment'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
